/**
 * Error type definitions for message API operations
 * Provides discriminated union types for different error scenarios
 * with retry strategies and recovery information
 */

export type MessageApiErrorCode =
  | "NETWORK_ERROR"
  | "TIMEOUT"
  | "AUTH_EXPIRED"
  | "AUTH_INVALID"
  | "FORBIDDEN"
  | "NOT_FOUND"
  | "VALIDATION_ERROR"
  | "CONFLICT"
  | "RATE_LIMITED"
  | "SERVER_ERROR"
  | "SERVICE_UNAVAILABLE"
  | "UNKNOWN_ERROR";

export type MessageApiErrorType =
  | "NetworkError"
  | "TimeoutError"
  | "AuthError"
  | "ValidationError"
  | "NotFoundError"
  | "ConflictError"
  | "RateLimitError"
  | "ServerError"
  | "UnknownError";

export interface BaseMessageApiError {
  type: MessageApiErrorType;
  code: MessageApiErrorCode;
  message: string;
  userMessage: string;
  statusCode?: number;
  shouldRetry: boolean;
  retryAfterMs?: number;
  originalError?: Error;
}

export interface NetworkError extends BaseMessageApiError {
  type: "NetworkError";
  code: "NETWORK_ERROR" | "TIMEOUT";
  shouldRetry: true;
  retryAfterMs: number;
}

export interface AuthError extends BaseMessageApiError {
  type: "AuthError";
  code: "AUTH_EXPIRED" | "AUTH_INVALID" | "FORBIDDEN";
  shouldRetry: false;
  requiresReauth: boolean;
}

export interface ValidationError extends BaseMessageApiError {
  type: "ValidationError";
  code: "VALIDATION_ERROR";
  shouldRetry: false;
  details?: Record<string, string>;
}

export interface NotFoundError extends BaseMessageApiError {
  type: "NotFoundError";
  code: "NOT_FOUND";
  shouldRetry: false;
  resourceType?: string;
  resourceId?: string;
}

export interface ConflictError extends BaseMessageApiError {
  type: "ConflictError";
  code: "CONFLICT";
  shouldRetry: true;
  retryAfterMs: number;
}

export interface RateLimitError extends BaseMessageApiError {
  type: "RateLimitError";
  code: "RATE_LIMITED";
  shouldRetry: true;
  retryAfterMs: number;
}

export interface ServerError extends BaseMessageApiError {
  type: "ServerError";
  code: "SERVER_ERROR" | "SERVICE_UNAVAILABLE";
  shouldRetry: true;
  retryAfterMs: number;
}

export interface UnknownError extends BaseMessageApiError {
  type: "UnknownError";
  code: "UNKNOWN_ERROR";
  shouldRetry: false;
}

export type MessageApiError =
  | NetworkError
  | AuthError
  | ValidationError
  | NotFoundError
  | ConflictError
  | RateLimitError
  | ServerError
  | UnknownError;

export function isNetworkError(error: MessageApiError): error is NetworkError {
  return error.type === "NetworkError";
}

export function isAuthError(error: MessageApiError): error is AuthError {
  return error.type === "AuthError";
}

export function isValidationError(error: MessageApiError): error is ValidationError {
  return error.type === "ValidationError";
}

export function isNotFoundError(error: MessageApiError): error is NotFoundError {
  return error.type === "NotFoundError";
}

export function isConflictError(error: MessageApiError): error is ConflictError {
  return error.type === "ConflictError";
}

export function isRateLimitError(error: MessageApiError): error is RateLimitError {
  return error.type === "RateLimitError";
}

export function isServerError(error: MessageApiError): error is ServerError {
  return error.type === "ServerError";
}

export function isRetryableError(error: MessageApiError): error is NetworkError | ConflictError | RateLimitError | ServerError {
  return error.shouldRetry;
}

export function isAuthRequiredError(error: MessageApiError): error is AuthError {
  return error.type === "AuthError";
}
